import { captureException } from '@sentry/browser';
import { ComponentRegistry } from './component-registry';
import { events } from '../events';
import { PlEvent } from '../models/pl.event';
import { UnsupportedBrowserError } from '../models/unsupported-browser.error';
import { toastError, toastInfo, toastWarn } from '../toasts';
import { Constructor } from '../types/constructor.type';
import { checkCompatibility } from '../utils/check-compatibility.fn';
import { createTransaction } from '../utils/create-transaction.fn';
import { getBuildNumber } from '../utils/get-build-number.fn';
import { isProd } from '../utils/is-prod.fn';
import { sentryInit } from '../utils/sentry-init.fn';
import { BaseComponent } from './base.component';
import { Module } from './module';

export abstract class Application {
  private modules: Module[] = [];

  constructor(
    protected appName: string,
    protected options: {
      declarations?: Constructor<BaseComponent<any>>[];
      modules?: Constructor<Module>[];
      tracesSampler?: (samplingContext: {
        transactionContext?: { op?: string };
      }) => number | boolean;
    }
  ) {}

  protected abstract onInit(): Promise<void>;

  async start() {
    const t = createTransaction(`Startup - ${this.appName}`, 'start');

    try {
      this.registerTelemetry();
      this.compatibilityCheck();
      this.declareComponents();
      this.registerModules();
      ComponentRegistry.bootstrap();

      await this.onInit();

      events.on('error', ({ error, errorPayload }) => {
        if (errorPayload instanceof Error) {
          captureException(errorPayload);
        } else {
          console.error(error, errorPayload, JSON.stringify(errorPayload));
          captureException(error, {
            extra: {
              errorPayload: JSON.stringify(errorPayload),
            },
          });
        }

        toastError('An unknown error occured, please reload the page', {
          close: false,
        });
      });

      events.on('warn', ({ message: err }) =>
        toastWarn(typeof err === 'string' ? err : err.message)
      );
      events.on('info', ({ message }) => toastInfo(message));

      t?.finish();
    } catch (e) {
      t?.setStatus('aborted').finish();

      if (e instanceof UnsupportedBrowserError) {
        captureException(e, {
          extra: {
            noFeedback: true,
          },
        });
      } else {
        captureException(e);
      }
    }
  }

  protected registerTelemetry() {
    try {
      sentryInit(isProd(), getBuildNumber(), (ctx) => this.tracesSampler(ctx));
    } catch (e) {
      console.error(e);
    }
  }

  protected compatibilityCheck() {
    if (!checkCompatibility()) {
      document.dispatchEvent(new PlEvent({ goal: 'unsupported browser' }));

      toastError(`We're sorry, your browser is not supported 🙁`, {
        close: false,
        duration: -1,
      });

      throw new UnsupportedBrowserError();
    }
  }

  protected declareComponents() {
    this.options?.declarations?.forEach((component) =>
      ComponentRegistry.declare(component)
    );
  }

  protected tracesSampler(samplingContext: {
    transactionContext?: { op?: string };
  }): number | boolean {
    if (typeof this.options?.tracesSampler === 'function') {
      return this.options.tracesSampler(samplingContext);
    }

    return 0.1;
  }

  protected async registerModules() {
    const modules = await Promise.all(
      this.options?.modules
        ?.map((m) => new m())
        .map(async (m) => {
          await m.register();
          return m;
        }) ?? []
    );

    this.modules.push(...modules);
  }
}
